import React, { useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { CircularProgress, makeStyles, Typography } from '@material-ui/core'
import { ToggleButton } from './toggle-button'
import { MenuIconWithDropdown } from './menu-icon-with-dropdown'
import { DEFAULT_ROLES } from '../../../constants'
import { Highlighted } from '../highlighted'
import { IsAssignedIcon } from '../'
import { UserPermissions } from 'web/src/services/permissions'
import { getLocationDevicesAmount } from '../../../../../utils'
import { isEmpty } from 'lodash'
import { LocationCreateModal } from '../../../../locations/components/location-create-modal'
import { HouseIcon } from 'lucide-react'
import { isLocationSelectionDisabled } from './tree-utils'

const stopClick = e => e.stopPropagation()

const MAX_NAME_LINES = 3

const useStyles = makeStyles(theme => ({
    disabled: {
        color: theme.quaternary,
        '&& *': {
            cursor: 'default'
        }
    },
    root: {
        paddingLeft: 4,
        borderTopLeftRadius: theme.borderRadius,
        borderBottomLeftRadius: theme.borderRadius
    },
    hoverOutline: {
        '&:hover': {
            backgroundColor: theme.tertiary,
            borderTopLeftRadius: theme.borderRadius,
            borderBottomLeftRadius: theme.borderRadius,
            '& #actionSlot': {
                visibility: 'visible'
            }
        },
        '& #actionSlot': {
            visibility: 'hidden'
        }
    },
    name: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flexShrink: 0
    },
    locationName: {
        // minWidth lets the clamped name shrink within the row.
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: MAX_NAME_LINES,
        overflow: 'hidden',
        overflowWrap: 'anywhere',
        minWidth: 0
    },
    row: {
        cursor: 'pointer',
        height: '100%',
        marginRight: 4,
        flex: 1,
        overflow: 'hidden',
        border: '2px solid transparent',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center'
    },
    locationsTreeLabelSelected: {
        fontWeight: 'bold',
        backgroundColor: theme.primary,
        color: theme.textPrimaryReverted,
        '& > div > div': {
            fontWeight: 'bold'
        }
    },
    respIcon: {
        position: 'absolute',
        left: '-1px',
        color: theme.active,
        fontWeight: 'bold',
        fontSize: 18
    },
    rightAlign: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
    },
    actionSlot: {
        marginLeft: 4,
        minWidth: 24
    },
    customerTitleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.textSecondary,
        padding: '0 10px'
    },
    customerTitle: {
        position: 'relative',
        textAlign: 'center',
        flex: 1,
        '&& span': {
            padding: '0 10px',
            display: 'inline-flex',
            background: '#fff',
            position: 'relative',
            zIndex: 1
        },

        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            zIndex: -1,
            borderTop: `1px dashed ${theme.textSecondary}`
        }
    },
    homeIcon: {
        marginRight: 4
    },
    isAssignedIcon: {
        marginRight: -4
    }
}))

export function LocationsTreeNode({
    permissions,
    node,
    style,
    onLocationPress,
    onMeasure,
    rightSlot,
    onAddSubLocation,
    setIsDevicesToMoveMode,
    setIsNetworkDevicesToMoveMode,
    locale,
    setIsDeleteLocationsModalOpen,
    setLocationsToDelete,
    actionDisabled,
    user,
    search,
    isMyLocationsTab,
    theme,
    pickerLocations,
    devicesAmountMap,
    locationsDevicesAmountMap,
    isLoadingDevicesAmountMap,
    withoutDevicesAmount
}) {
    const classes = useStyles()
    const data = node.data
    const hasChildren = node.isInternal
    const open = node.isOpen
    const isSelected = node.isSelected
    const location = data?.location
    const locationId = location?.id
    const isAssigned = location?.is_assigned
    const isCustomerTitle = location?.is_customer_title
    const isBottomPaddingNode = location?.is_bottom_padding_node
    const isOriginal = location?.is_original
    const customerName = location?.customer_name
    const locationName = location?.name
    const isDisabled = isLocationSelectionDisabled(data)
    const [isLocationCreateModalOpen, setIsLocationCreateModalOpen] =
        useState(false)
    const nameRef = useRef(null)

    // Re-measure after each render to catch text and sidebar-width changes.
    useLayoutEffect(() => {
        if (nameRef.current && onMeasure) {
            onMeasure(node.id, nameRef.current.offsetHeight)
        }
    })

    const handleClick = e => {
        e.stopPropagation()
        if (isDisabled) {
            return
        }
        onLocationPress(locationId)
        node.handleClick(e)
    }

    const handleToggle = event => {
        event.stopPropagation()
        node.toggle()
    }

    if (isCustomerTitle) {
        return (
            <div
                style={{ height: '100%', width: '100%' }}
                onClick={stopClick}
                className={classes.customerTitleRow}>
                <div className={classes.customerTitle}>
                    <span>
                        {isOriginal && (
                            <HouseIcon size={20} className={classes.homeIcon} />
                        )}
                        {customerName}
                    </span>
                </div>
            </div>
        )
    }

    if (isBottomPaddingNode) {
        return (
            <div
                style={{ height: '100%', width: '100%' }}
                onClick={stopClick}></div>
        )
    }

    return (
        <>
            <div
                style={{ height: '100%', width: '100%' }}
                onClick={handleClick}
                className={classNames(classes.root, {
                    [classes.hoverOutline]: !isSelected && !isDisabled,
                    [classes.locationsTreeLabelSelected]: isSelected,
                    [classes.disabled]: isDisabled
                })}>
                <div style={style} className={classes.row}>
                    <ToggleButton
                        toggle={handleToggle}
                        isOpen={open}
                        hasChildren={hasChildren}
                        isSelected={isSelected}
                    />
                    <Typography
                        variant="inherit"
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0
                        }}>
                        <span
                            ref={nameRef}
                            className={classes.locationName}
                            style={{ flex: 1 }}>
                            <Highlighted
                                text={locationName}
                                highlight={search}
                            />
                        </span>
                        {!withoutDevicesAmount ? (
                            <>
                                {typeof search === 'string' &&
                                search.length > 0 ? (
                                    isLoadingDevicesAmountMap ? (
                                        <div
                                            style={{
                                                paddingLeft: 4,
                                                paddingRight: 4,
                                                opacity: 0.7,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                            <CircularProgress
                                                size={16}
                                                color={theme.primary}
                                            />
                                        </div>
                                    ) : (
                                        <span
                                            className={classes.name}
                                            style={{
                                                paddingLeft: 4,
                                                paddingRight: 4,
                                                opacity: 0.4
                                            }}>
                                            {!isEmpty(devicesAmountMap) &&
                                                devicesAmountMap.hasOwnProperty(
                                                    location.id
                                                ) &&
                                                `(${
                                                    devicesAmountMap[
                                                        location.id
                                                    ]
                                                })`}
                                        </span>
                                    )
                                ) : (
                                    <span
                                        className={classes.name}
                                        style={{
                                            paddingLeft: 4,
                                            paddingRight: 4,
                                            opacity: 0.4
                                        }}>
                                        {`(${getLocationDevicesAmount(
                                            location
                                        )})`}
                                    </span>
                                )}
                            </>
                        ) : null}
                    </Typography>
                    <div className={classes.rightAlign}>
                        {!isAssigned && isDisabled && (
                            <IsAssignedIcon
                                theme={theme}
                                iconClass={classes.isAssignedIcon}
                                isInverted={isSelected}
                                locale={locale}
                                isHidden={
                                    (UserPermissions.isAllOriginalGranted() &&
                                        !isAssigned) ||
                                    isMyLocationsTab
                                }
                                isLocked
                            />
                        )}
                        {rightSlot && !isDisabled ? (
                            <>
                                {typeof search === 'string' &&
                                search.length > 0 ? (
                                    <div>
                                        {rightSlot(
                                            locationsDevicesAmountMap?.[
                                                location?.id
                                            ] || {}
                                        )}
                                    </div>
                                ) : (
                                    <div>{rightSlot(location || {})}</div>
                                )}
                            </>
                        ) : null}
                        <div id="actionSlot" className={classes.actionSlot}>
                            {!isDisabled && !actionDisabled && (
                                <MenuIconWithDropdown
                                    disabled={actionDisabled}
                                    permissions={permissions}
                                    location={location}
                                    isSelected={isSelected}
                                    onAddSubLocation={onAddSubLocation}
                                    onLocationLabelClick={onLocationPress}
                                    setIsDevicesToMoveMode={
                                        setIsDevicesToMoveMode
                                    }
                                    setIsNetworkDevicesToMoveMode={
                                        setIsNetworkDevicesToMoveMode
                                    }
                                    locale={locale}
                                    setIsDeleteLocationsModalOpen={
                                        setIsDeleteLocationsModalOpen
                                    }
                                    setLocationsToDelete={setLocationsToDelete}
                                    pickerLocations={pickerLocations}
                                    setIsLocationCreateModalOpen={
                                        setIsLocationCreateModalOpen
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isLocationCreateModalOpen && (
                <LocationCreateModal
                    onClose={() => {
                        setIsLocationCreateModalOpen(false)
                    }}
                    modalProps={{
                        open: isLocationCreateModalOpen
                    }}
                    parentId={locationId}
                    pickerLocations={pickerLocations}
                    parentLocation={location}
                />
            )}
        </>
    )
}
